import React, { useCallback, useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api, { API } from "@/lib/api";
import { authFileClick } from "@/lib/openFile";
import ClientShell from "@/components/ClientShell";
import { ArrowLeft, CheckCircle, XCircle, FileText, DownloadSimple } from "@phosphor-icons/react";
import { formatDate, formatCurrency, formatDateTime } from "@/lib/format";
import { useCategories } from "@/hooks/useCategories";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const STATUS_META = {
  PENDING_REVIEW: { label: "Pending review", tone: "bg-zinc-100 text-zinc-800" },
  IN_REVIEW: { label: "In review", tone: "bg-amber-50 text-amber-800" },
  QUOTED: { label: "Quote ready", tone: "bg-[#FF5A1F]/10 text-[#E04814]" },
  APPROVED: { label: "Approved", tone: "bg-green-50 text-green-800" },
  REJECTED: { label: "Rejected", tone: "bg-red-50 text-red-800" },
};

export default function QuotationDetail() {
  const { quotationId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { labelFor } = useCategories();
  const [q, setQ] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const fetchQ = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/quotations/${quotationId}`);
      setQ(res.data);
    } catch (e) {
      console.error("[QuotationDetail] failed:", e);
    }
    setLoading(false);
  }, [quotationId]);

  useEffect(() => { fetchQ(); }, [fetchQ]);

  const act = async (kind) => {
    setBusy(true);
    try {
      const res = await api.post(`/quotations/${quotationId}/${kind}`);
      if (kind === "approve") {
        const orderNum = res?.data?.order?.order_number;
        const orderId = res?.data?.order?.order_id;
        toast.success(
          orderNum
            ? `Quote approved — Order ${orderNum} created and now in Active Orders`
            : "Quote approved"
        );
        if (orderId) {
          navigate(`/orders/${orderId}`);
          return;
        }
      } else {
        toast.success("Quote rejected");
      }
      fetchQ();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Failed");
    }
    setBusy(false);
  };

  if (loading) {
    return <ClientShell><div className="h-96 bg-white border border-zinc-200 animate-pulse" /></ClientShell>;
  }
  if (!q) {
    return (
      <ClientShell>
        <div className="bg-white border border-zinc-200 p-12 text-center">
          <div className="text-zinc-500">Quotation not found</div>
          <button onClick={() => navigate(-1)} className="mt-4 underline">Go back</button>
        </div>
      </ClientShell>
    );
  }

  const meta = STATUS_META[q.status] || STATUS_META.PENDING_REVIEW;
  const canDecide = q.status === "QUOTED" && (user?.role === "client");

  return (
    <ClientShell>
      <Link
        to="/quotations"
        data-testid="quotation-back-link"
        className="inline-flex items-center gap-2 mt-tiny text-zinc-500 hover:text-zinc-900 mb-6"
      >
        <ArrowLeft size={14} /> Back to quotations
      </Link>

      <div className="bg-white border border-zinc-200 p-6 md:p-8">
        <div className="flex flex-wrap items-baseline gap-3">
          <div className="mt-tiny text-zinc-400 font-mono-mt">{q.quotation_number}</div>
          <span className={`inline-flex items-center px-2.5 py-1 text-[0.65rem] font-semibold uppercase tracking-[0.15em] ${meta.tone}`}>
            {meta.label}
          </span>
        </div>
        <h1 className="font-display text-3xl md:text-4xl font-black text-zinc-900 mt-2">{q.material}</h1>
        <div className="mt-1 text-sm text-zinc-500">{labelFor(q.category)} · Submitted {formatDateTime(q.created_at)}</div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-8">
          <Cell label="Quantity" value={q.quantity || "—"} />
          <Cell label="Dimensions" value={q.dimensions || "—"} />
          <Cell label="Project" value={q.project_name || "—"} />
          <Cell label="Client" value={q.client_name} />
        </div>

        {q.specifications && Object.keys(q.specifications).length > 0 && (
          <div className="mt-6" data-testid="quotation-specifications">
            <div className="mt-tiny text-zinc-400 mb-2">Selected options</div>
            <div className="flex flex-wrap gap-2">
              {Object.entries(q.specifications).map(([k, v]) => (
                <span key={k} className="inline-flex items-center gap-1.5 border border-zinc-200 bg-zinc-50 px-3 py-1.5 text-sm">
                  <span className="text-zinc-500 text-xs uppercase tracking-wide">{k}</span>
                  <b className="text-zinc-900">{String(v)}</b>
                </span>
              ))}
            </div>
          </div>
        )}

        {q.notes && (
          <div className="mt-6 border-l-2 border-zinc-300 pl-4 py-1">
            <div className="mt-tiny text-zinc-400">Your notes</div>
            <div className="mt-1 text-sm text-zinc-700">{q.notes}</div>
          </div>
        )}
      </div>

      {/* Quote block */}
      {(q.status === "QUOTED" || q.status === "APPROVED" || q.status === "REJECTED") && (
        <div className={`mt-6 border p-6 md:p-8 ${q.status === "QUOTED" ? "border-[#FF5A1F]" : "border-zinc-200"} bg-white`}>
          <div className="mt-tiny text-[#FF5A1F]">Our quote</div>
          <div className="mt-2 flex items-baseline gap-4 flex-wrap">
            <div className="font-display text-4xl md:text-5xl font-black text-zinc-900">
              {formatCurrency(q.quote_amount)}
            </div>
            {q.quote_valid_until && (
              <div className="mt-tiny text-zinc-500">Valid until {formatDate(q.quote_valid_until)}</div>
            )}
          </div>
          {q.quote_notes && (
            <div className="mt-4 text-zinc-700 leading-relaxed">{q.quote_notes}</div>
          )}

          {/* Attached files */}
          {(q.files || []).length > 0 && (
            <div className="mt-6 pt-6 border-t border-zinc-200">
              <div className="mt-tiny text-zinc-400 mb-3">Certificates & attachments</div>
              <div className="grid gap-2">
                {q.files.map((f) => (
                  <a
                    key={f.file_id}
                    href={`${API}/files/${f.storage_path}`}
                    target="_blank"
                    rel="noreferrer"
                    onClick={authFileClick(f.storage_path)}
                    data-testid={`quote-file-${f.file_id}`}
                    className="flex items-center justify-between border border-zinc-200 hover:border-zinc-900 hover:bg-zinc-50 p-3 transition-colors"
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <FileText size={18} className="text-[#0047AB] shrink-0" weight="duotone" />
                      <div className="min-w-0">
                        <div className="text-sm text-zinc-900 truncate">{f.original_filename}</div>
                        <div className="mt-tiny text-zinc-400">{f.kind.replace("_", " ")}</div>
                      </div>
                    </div>
                    <DownloadSimple size={18} className="text-zinc-400" />
                  </a>
                ))}
              </div>
            </div>
          )}

          {canDecide && (
            <div className="mt-6 pt-6 border-t border-zinc-200 flex flex-wrap gap-3">
              <button
                disabled={busy}
                onClick={() => act("approve")}
                data-testid="approve-quote-button"
                className="inline-flex items-center gap-2 bg-[#0047AB] hover:bg-[#003380] text-white px-6 py-3 font-medium transition-colors disabled:opacity-60"
              >
                <CheckCircle size={18} weight="fill" /> Approve quote
              </button>
              <button
                disabled={busy}
                onClick={() => act("reject")}
                data-testid="reject-quote-button"
                className="inline-flex items-center gap-2 border border-zinc-300 hover:border-red-500 hover:text-red-500 px-6 py-3 font-medium transition-colors disabled:opacity-60"
              >
                <XCircle size={18} /> Reject
              </button>
            </div>
          )}

          {q.status === "APPROVED" && q.order_number && (
            <div className="mt-6 pt-6 border-t border-zinc-200">
              <div className="mt-tiny text-green-700">Order created</div>
              <Link
                to={q.order_id ? `/orders/${q.order_id}` : "/orders"}
                data-testid="view-generated-order-link"
                className="mt-2 inline-flex items-center gap-2 text-[#0047AB] hover:underline font-medium"
              >
                Track order {q.order_number} <ArrowLeft size={14} className="rotate-180" />
              </Link>
            </div>
          )}
        </div>
      )}
    </ClientShell>
  );
}

function Cell({ label, value }) {
  return (
    <div>
      <div className="mt-tiny text-zinc-400">{label}</div>
      <div className="font-medium text-zinc-900 mt-1">{value}</div>
    </div>
  );
}
